import { create } from 'zustand';
import { serverService } from '../react-app/services/apiService.js';
import { DEMO_MEMBERS } from './memberStore.js';

const DEMO_SERVERS = [
  {
    id: 'demo-foundation',
    name: 'AeThex Foundation',
    description: 'Core infrastructure, security & authentication services',
    icon_url: null,
    _demo: true,
    _channels: [
      { id: 'demo-f-general', name: 'general', category: 'Announcements', type: 'text', slowmode: 5 },
      { id: 'demo-f-auth', name: 'authentication', category: 'Development', type: 'text' },
      { id: 'demo-f-security', name: 'security', category: 'Development', type: 'text' },
      { id: 'demo-f-deploy', name: 'deployments', category: 'Development', type: 'text' },
      { id: 'demo-f-support', name: 'support', category: 'Support', type: 'text' },
      { id: 'demo-f-voice',   name: 'Nexus Lounge',  category: 'Voice Channels', type: 'voice' },
      { id: 'demo-f-announce', name: 'announcements', category: 'Announcements', type: 'announcement' },
      { id: 'demo-f-forum',    name: 'help-forum',    category: 'Support',        type: 'forum' },
      { id: 'demo-f-stage',    name: 'Community Stage', category: 'Voice Channels', type: 'stage' },
    ],
  },
  {
    id: 'demo-labs',
    name: 'AeThex Labs',
    description: 'Experiments, research & bleeding-edge features',
    icon_url: null,
    _demo: true,
    _channels: [
      { id: 'demo-l-general', name: 'general', category: 'General', type: 'text' },
      { id: 'demo-l-experiments', name: 'experiments', category: 'Research', type: 'text' },
      { id: 'demo-l-nexus', name: 'nexus-sdk', category: 'Research', type: 'text' },
      { id: 'demo-l-ai', name: 'ai-research', category: 'Research', type: 'text' },
      { id: 'demo-l-voice', name: 'Lab Voice', category: 'Voice Channels', type: 'voice' },
    ],
  },
  {
    id: 'demo-corp',
    name: 'AeThex Corporation',
    description: 'Production tools, enterprise services & partner network',
    icon_url: null,
    _demo: true,
    _channels: [
      { id: 'demo-c-general', name: 'general', category: 'Announcements', type: 'text' },
      { id: 'demo-c-partners', name: 'partners', category: 'Business', type: 'text' },
      { id: 'demo-c-releases', name: 'releases', category: 'Business', type: 'text' },
      { id: 'demo-c-support', name: 'enterprise-support', category: 'Support', type: 'text' },
      { id: 'demo-c-voice', name: 'Board Room', category: 'Voice Channels', type: 'voice' },
    ],
  },
];

export const useServerStore = create((set, get) => ({
  servers: [],
  currentServerId: null,
  isLoading: false,
  error: null,

  fetchServers: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await serverService.list();
      const serversList = response.data || response;
      const realServers = Array.isArray(serversList) ? serversList : [];

      // Use real servers if any exist, otherwise show demo servers
      const finalServers = realServers.length > 0 ? realServers : DEMO_SERVERS;

      set({ servers: finalServers, isLoading: false });

      if (!get().currentServerId) {
        const first = finalServers[0];
        set({ currentServerId: first.id });
        const channelStore = await import('./channelStore.js');
        if (first._demo) {
          const { useMemberStore } = await import('./memberStore.js');
          channelStore.useChannelStore.getState().loadDemoChannels(first._channels);
          useMemberStore.getState().setMembers(DEMO_MEMBERS);
        } else {
          channelStore.useChannelStore.getState().fetchChannels(first.id);
        }
      }
    } catch (error) {
      // API failed — still show demo servers so the UI isn't blank
      set({ servers: DEMO_SERVERS, isLoading: false, error: error.message });
      if (!get().currentServerId) {
        set({ currentServerId: DEMO_SERVERS[0].id });
        const channelStore = await import('./channelStore.js');
        const { useMemberStore } = await import('./memberStore.js');
        channelStore.useChannelStore.getState().loadDemoChannels(DEMO_SERVERS[0]._channels);
        useMemberStore.getState().setMembers(DEMO_MEMBERS);

      }
    }
  },

  setCurrentServer: (serverId) => {
    set({ currentServerId: serverId });
    // Clear server unread dot when switching to this server
    import('./channelStore.js').then((m) => {
      m.useChannelStore.getState().clearServerUnread(serverId);
    });
    const server = get().servers.find((s) => s.id === serverId);
    if (server?._demo) {
      import('./channelStore.js').then((m) => {
        m.useChannelStore.getState().loadDemoChannels(server._channels);
      });
      import('./memberStore.js').then(({ useMemberStore }) => {
        useMemberStore.getState().setMembers(DEMO_MEMBERS);
      });
    } else {
      import('./channelStore.js').then((m) => {
        m.useChannelStore.getState().fetchChannels(serverId);
      });
      // MemberSidebar fetches real members via API on currentServerId change
    }
  },

  getCurrentServer: () => {
    const { servers, currentServerId } = get();
    return servers.find((s) => s.id === currentServerId);
  },

  addServer: (server) =>
    set((state) => ({
      servers: [...state.servers.filter((s) => !s._demo), server],
    })),

  removeServer: (serverId) =>
    set((state) => ({
      servers: state.servers.filter((s) => s.id !== serverId),
      currentServerId:
        state.currentServerId === serverId ? null : state.currentServerId,
    })),

  updateServer: (serverId, updates) =>
    set((state) => ({
      servers: state.servers.map((s) =>
        s.id === serverId ? { ...s, ...updates } : s
      ),
    })),
}));
